import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useMatch } from "@/hooks/useMatch";
import { supabase } from "@/integrations/supabase/client";
import { useDealbreakerQuestions } from "@/hooks/useDealbreakerQuestions";
import { useDealbreakerAnswers, useSubmitDealbreakerAnswers } from "@/hooks/useDealbreakerAnswers";
import { DealbreakerAnswerSubmit } from "@/types/dealbreaker";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { Footer } from "@/components/Footer";
import { MatchProfileDetail } from "@/components/matches/MatchProfileDetail";
import { ContactInfoCard } from "@/components/matches/ContactInfoCard";
import { DealbreakerQuestionsCard } from "@/components/matches/DealbreakerQuestionsCard";
import { DealbreakerAnswersView } from "@/components/matches/DealbreakerAnswersView";
import { TheirAnswersCard } from "@/components/matches/TheirAnswersCard";
import { ReportUserDialog } from "@/components/ReportUserDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Heart, X, Info, ArrowLeft, Flag } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";

export default function MatchDetail() {
  const { matchId } = useParams<{ matchId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { match, loading, error, refetch } = useMatch(matchId, user?.id);
  const [responding, setResponding] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);

  // Get other profile ID
  const otherProfileId = match?.profile_1_id === user?.id ? match?.profile_2_id : match?.profile_1_id;

  // Fetch dealbreaker questions for the other profile
  const { data: dealbreakerQuestions = [] } = useDealbreakerQuestions(otherProfileId);

  // Fetch dealbreaker questions for my profile
  const { data: myDealbreakerQuestions = [] } = useDealbreakerQuestions(user?.id);

  // Fetch existing answers for this match (my answers to their questions)
  const { data: existingAnswers = [], refetch: refetchAnswers } = useDealbreakerAnswers(matchId, user?.id);

  // Fetch all answers for this match (for display after both accept)
  const { data: allMatchAnswers = [] } = useDealbreakerAnswers(matchId);

  // Submit answers mutation
  const { mutateAsync: submitAnswers, isPending: submittingAnswers } = useSubmitDealbreakerAnswers();

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      
      const { data } = await supabase
        .from("profiles")
        .select("first_name, profile_picture_url")
        .eq("id", user.id)
        .maybeSingle();
      
      if (data) setUserProfile(data);
    };

    fetchUserProfile();
  }, [user]);

  const handleSubmitAnswers = async (answers: any[]) => {
    if (!matchId || !user) return;

    const answersToSubmit: DealbreakerAnswerSubmit[] = answers.map((a) => ({
      match_id: matchId,
      question_id: a.question_id,
      answer_text: a.answer_text,
      answered_by_profile_id: user.id,
    }));

    await submitAnswers(answersToSubmit);
    await refetchAnswers();
  };

  const handleResponse = async (response: "accepted" | "rejected") => {
    if (!match || !user) return;

    setResponding(true);

    try {
      const isProfile1 = match.profile_1_id === user.id;
      const updateField = isProfile1 ? "profile_1_response" : "profile_2_response";
      const dateField = isProfile1 ? "profile_1_response_date" : "profile_2_response_date";

      const { error: updateError } = await supabase
        .from("matches")
        .update({
          [updateField]: response,
          [dateField]: new Date().toISOString(),
        })
        .eq("id", match.id);

      if (updateError) throw updateError;

      toast.success(
        response === "accepted"
          ? "Match accepted! Waiting for the other party's response."
          : "Match declined."
      );

      await refetch();
    } catch (err: any) {
      console.error("match.respond_failed");
      toast.error("Failed to respond to match. Please try again.");
    } finally {
      setResponding(false);
      setShowAcceptDialog(false);
      setShowDeclineDialog(false);
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <DashboardSidebar />
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (error || !match) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <DashboardSidebar />
          <div className="flex-1 flex items-center justify-center p-6">
            <Card>
              <CardHeader>
                <CardTitle>Error</CardTitle>
                <CardDescription>{error || "Match not found"}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => navigate("/dashboard/matches")}>
                  Back to Matches
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  const isProfile1 = match.profile_1_id === user?.id;
  const otherProfile = isProfile1 ? match.profile_2 : match.profile_1;
  const myResponse = isProfile1 ? match.profile_1_response : match.profile_2_response;
  const theirResponse = isProfile1 ? match.profile_2_response : match.profile_1_response;
  const otherContactInfo = isProfile1 ? match.contact_info_2 : match.contact_info_1;
  const myContactInfo = isProfile1 ? match.contact_info_1 : match.contact_info_2;

  const getStatusBadge = () => {
    if (match.status === "both_accepted") {
      return <Badge className="bg-green-500">Both Accepted</Badge>;
    }
    if (match.status === "rejected") {
      return <Badge variant="destructive">Declined</Badge>;
    }
    if (myResponse === "accepted") {
      return <Badge variant="secondary">You Accepted - Awaiting Response</Badge>;
    }
    if (theirResponse === "accepted") {
      return <Badge variant="secondary">They Accepted - Awaiting Your Response</Badge>;
    }
    return <Badge variant="outline">Pending Response</Badge>;
  };

  // Check if user has answered all dealbreaker questions
  const hasQuestions = dealbreakerQuestions && dealbreakerQuestions.length > 0;
  const hasAnsweredAllQuestions = hasQuestions
    ? existingAnswers && existingAnswers.length === dealbreakerQuestions.length
    : true;

  const canRespond = !myResponse && match.status !== "rejected";
  const canAccept = canRespond && hasAnsweredAllQuestions;

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <DashboardSidebar />
        
        <div className="flex-1 flex flex-col">
          <DashboardHeader
            leftContent={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard/matches")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Matches
              </Button>
            }
            profilePicture={userProfile?.profile_picture_url}
            firstName={userProfile?.first_name}
          />

          <main className="flex-1 overflow-y-auto">
            <div className="container max-w-4xl mx-auto p-6 space-y-6">
              {/* Header */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h1 className="text-3xl font-bold">
                    {match.status === "both_accepted" 
                      ? "Successful Match!" 
                      : "Match Notification"}
                  </h1>
                  {getStatusBadge()}
                </div>
                <p className="text-muted-foreground">
                  Received {formatDistanceToNow(new Date(match.created_at), { addSuffix: true })}
                </p>
              </div>

              {/* Islamic Reminder */}
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Approaching with Good Intentions</AlertTitle>
                <AlertDescription>
                  Remember to approach this process with sincerity and the intention of marriage. 
                  May Allah guide you both toward what is best. If both parties accept, contact 
                  information will be shared to continue conversations with proper Islamic etiquette.
                </AlertDescription>
              </Alert>

              {/* Match Profile Details */}
              {otherProfile ? (
                <Card>
                  <CardContent className="pt-6">
                    <MatchProfileDetail profile={otherProfile} />
                    {otherProfileId && (
                      <div className="mt-6 pt-4 border-t flex justify-end">
                        <ReportUserDialog
                          reportedUserId={otherProfileId}
                          role="candidate"
                          trigger={
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Flag className="h-3.5 w-3.5 mr-1.5" />
                              Report this profile
                            </Button>
                          }
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Alert variant="destructive">
                  <Info className="h-4 w-4" />
                  <AlertTitle>Profile Information Unavailable</AlertTitle>
                  <AlertDescription>
                    We're unable to load the complete profile information for this match.
                    This may be due to incomplete profile data or access restrictions.
                    You can still respond to this match, but please note that profile details are limited.
                  </AlertDescription>
                </Alert>
              )}

              {/* Their Answers to My Questions - shown anytime */}
              {myDealbreakerQuestions.length > 0 && user && (
                <TheirAnswersCard
                  myQuestions={myDealbreakerQuestions}
                  theirAnswers={allMatchAnswers.filter(
                    (a) => a.answered_by_profile_id === otherProfileId
                  )}
                />
              )}

              {/* Dealbreaker Questions */}
              {hasQuestions && canRespond && user && matchId && (
                <DealbreakerQuestionsCard
                  questions={dealbreakerQuestions}
                  existingAnswers={existingAnswers}
                  matchId={matchId}
                  currentUserId={user.id}
                  onSubmit={handleSubmitAnswers}
                  submitting={submittingAnswers}
                  disabled={myResponse !== null}
                />
              )}

              {/* Action Buttons */}
              {canRespond && (
                <Card>
                  <CardHeader>
                    <CardTitle>Your Response</CardTitle>
                    <CardDescription>
                      {hasQuestions && !hasAnsweredAllQuestions
                        ? "Please answer all dealbreaker questions above before accepting"
                        : "Please review the information above and decide if you'd like to proceed"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4">
                      <Button
                        onClick={() => setShowAcceptDialog(true)}
                        disabled={responding || !canAccept}
                        className="flex-1"
                        size="lg"
                        title={!hasAnsweredAllQuestions ? "Please answer all dealbreaker questions first" : ""}
                      >
                        <Heart className="mr-2 h-5 w-5" />
                        Accept Match
                      </Button>
                      <Button
                        onClick={() => setShowDeclineDialog(true)}
                        disabled={responding}
                        variant="outline"
                        className="flex-1"
                        size="lg"
                      >
                        <X className="mr-2 h-5 w-5" />
                        Decline Match
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Status Info */}
              {myResponse && match.status !== "both_accepted" && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>
                    {myResponse === "accepted" 
                      ? "Waiting for their response" 
                      : "Match declined"}
                  </AlertTitle>
                  <AlertDescription>
                    {myResponse === "accepted"
                      ? "We'll notify you when the other party responds to this match."
                      : "This match has been declined and will be marked as inactive."}
                  </AlertDescription>
                </Alert>
              )}

              {/* Contact Information (shown when both accepted) */}
              {match.status === "both_accepted" && otherContactInfo && myContactInfo && otherProfile && (
                <div className="space-y-4">
                  <Separator />
                  <h2 className="text-2xl font-bold">Contact Information</h2>
                  
                  <ContactInfoCard
                    contactInfo={otherContactInfo}
                    name={otherProfile.first_name}
                  />

                  <Alert className="bg-primary/10 border-primary/20">
                    <Info className="h-4 w-4" />
                    <AlertTitle>Next Steps</AlertTitle>
                    <AlertDescription>
                      You may now contact each other directly using the information above.
                      Please proceed with conversations in accordance with Islamic guidelines.
                      We recommend involving family members and a wali when appropriate.
                    </AlertDescription>
                  </Alert>

                  {/* Dealbreaker Answers (shown after both accepted) */}
                  {user && otherProfileId && (
                    <DealbreakerAnswersView
                      myQuestions={myDealbreakerQuestions}
                      theirAnswersToMyQuestions={allMatchAnswers.filter(
                        (a) => a.answered_by_profile_id === otherProfileId
                      )}
                      theirQuestions={dealbreakerQuestions}
                      myAnswersToTheirQuestions={allMatchAnswers.filter(
                        (a) => a.answered_by_profile_id === user.id
                      )}
                      myProfileId={user.id}
                      theirProfileId={otherProfileId}
                    />
                  )}
                </div>
              )}

              {/* Report the matchmaker who set up this match */}
              {match.created_by_matchmaker_id && (
                <div className="pt-2 flex justify-end">
                  <ReportUserDialog
                    reportedUserId={match.created_by_matchmaker_id}
                    role="matchmaker"
                    trigger={
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Flag className="h-3.5 w-3.5 mr-1.5" />
                        Report the matchmaker who set up this match
                      </Button>
                    }
                  />
                </div>
              )}
            </div>
          </main>
          <Footer />
        </div>
      </div>

      {/* Accept Dialog */}
      <AlertDialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Accept This Match?</AlertDialogTitle>
            <AlertDialogDescription>
              By accepting, you express interest in connecting with this person. If they also accept, 
              your contact information will be shared with each other.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={responding}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleResponse("accepted")}
              disabled={responding}
            >
              {responding ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                "Yes, Accept Match"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Decline Dialog */}
      <AlertDialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline This Match?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to decline this match? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={responding}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleResponse("rejected")}
              disabled={responding}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {responding ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                "Yes, Decline Match"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
}
